import { useNavigate } from 'react-router-dom';
import { Star, X, Coffee, MessageSquare, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';

function StarDisplay({ rating, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= Math.round(rating) ? 'fill-accent text-accent' : 'text-border'}
        />
      ))}
    </div>
  );
}

export default function ExistingCoffeeDialog({ result, onClose }) {
  useBodyScrollLock();
  const navigate = useNavigate();
  const { coffee, reviewCount, recentReviews } = result;

  const handleViewDetail = () => {
    navigate(`/coffee/${coffee.id}`);
  };

  const handleAddReview = () => {
    navigate(`/coffee/${coffee.id}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-foreground/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full bg-card rounded-t-3xl border-t border-border p-5 pb-10 space-y-5 max-h-[85vh] overflow-y-auto overscroll-contain max-w-lg mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div className="w-10 h-1 bg-border rounded-full mx-auto" />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
              <Coffee size={16} className="text-accent" />
            </div>
            <h2 className="font-heading text-base font-semibold">This coffee is already listed</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X size={20} />
          </button>
        </div>

        {/* Coffee Info Card */}
        <div className="bg-secondary/50 rounded-2xl p-4 border border-border space-y-2">
          <div className="flex items-start gap-3">
            {coffee.photo_url ? (
              <img src={coffee.photo_url} alt={coffee.name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center shrink-0">
                <Coffee size={20} className="text-muted-foreground" strokeWidth={1.5} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm leading-tight">{coffee.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{coffee.cafe_name}</p>
              {coffee.cafe_area && (
                <p className="text-xs text-muted-foreground">{coffee.cafe_area}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            {coffee.average_rating > 0 ? (
              <>
                <StarDisplay rating={coffee.average_rating} />
                <span className="text-sm font-semibold">{coffee.average_rating.toFixed(1)}</span>
              </>
            ) : (
              <span className="text-xs text-muted-foreground">No ratings yet</span>
            )}
            <div className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
              <MessageSquare size={12} />
              <span>{reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}</span>
            </div>
          </div>
        </div>

        {/* Recent Reviews Preview */}
        {recentReviews && recentReviews.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Recent Reviews</p>
            {recentReviews.map((review, i) => (
              <div key={i} className="bg-background rounded-xl p-3 border border-border space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold">{review.reviewer_name || 'Anonymous'}</p>
                  <StarDisplay rating={review.rating} size={12} />
                </div>
                {review.tasting_notes && (
                  <p className="text-xs text-accent font-medium">{review.tasting_notes}</p>
                )}
                {review.comment && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{review.comment}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* CTAs */}
        <div className="space-y-2 pt-1">
          <Button onClick={handleAddReview} className="w-full rounded-xl">
            Add a Review
            <ArrowRight size={15} className="ml-2" />
          </Button>
          <Button onClick={handleViewDetail} variant="outline" className="w-full rounded-xl text-sm">
            See all reviews ({reviewCount})
          </Button>
        </div>
      </div>
    </div>
  );
}